
/**
 * UserTraining Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class UserTrainingService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'UserTraining listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch user-training list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch user-training');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'UserTraining oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create user-training');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'UserTraining güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update user-training');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'UserTraining silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete user-training');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Custom operation: getMyTrainings
     */
    async getMyTrainings(data = {}) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/my-trainings`, { params: data });
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute getMyTrainings');
        }
    }

    /**
     * Archive training
     */
    async archive(id) {
        try {
            const response = await this.api.update(id, { isArchived: true });
            return {
                success: true,
                data: response.data,
                message: 'Eğitim arşivlendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Eğitim arşivlenemedi');
        }
    }

    /**
     * Unarchive training
     */
    async unarchive(id) {
        try {
            const response = await this.api.client.patch(`${this.api.basePath}/${id}`, { isArchived: false });
            return {
                success: true,
                data: response.data,
                message: response.message || 'Eğitim aktif hale getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unarchive training');
        }
    }

    /**
     * Custom operation: createSubmit
     */
    async createSubmit(id, data = {}) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/submit`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createSubmit');
        }
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Mark video as watched
     */
    async markVideoAsWatched(id, data = {}) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/mark-watched`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to mark video as watched');
        }
    }

    /**
     * Mark content module complete (video or text page)
     */
    async markContentProgress(id, data = {}) {
        try {
            const response = await this.api.client.post(
                `${this.api.basePath}/${id}/content-progress`,
                data,
            );
            const body = response.data || {};
            return {
                success: true,
                data: body.data ?? body,
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update content progress');
        }
    }

    /**
     * Update progress
     */
    async updateProgress(id, data = {}) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/progress`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update progress');
        }
    }

    /**
     * Submit evaluation
     */
    async submitEvaluation(id, answers) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/evaluation-submit`, { answers });
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to submit evaluation');
        }
    }

    async getSurveyTasks(id) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/${id}/survey-tasks`);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to load survey tasks');
        }
    }

    async getEvaluationBoard(trainingId) {
        try {
            const response = await this.api.client.get(
                `${this.api.basePath}/evaluation-board/${trainingId}`,
            );
            const body = response.data || {};
            return {
                success: true,
                data: body.data ?? body,
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to load evaluation board');
        }
    }

    async saveEvaluationScores(id, payload) {
        try {
            const response = await this.api.client.patch(
                `${this.api.basePath}/${id}/evaluation-scores`,
                payload,
            );
            const body = response.data || {};
            return {
                success: true,
                data: body.data ?? body,
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to save evaluation scores');
        }
    }

    async getEvaluationResult(id) {
        try {
            const response = await this.api.client.get(
                `${this.api.basePath}/${id}/evaluation-result`,
            );
            const body = response.data || {};
            return {
                success: true,
                data: body.data ?? body,
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to load evaluation result');
        }
    }

    async approveEvaluation(id, payload = {}) {
        try {
            const response = await this.api.client.post(
                `${this.api.basePath}/${id}/evaluation-approve`,
                payload,
            );
            const body = response.data || {};
            return {
                success: true,
                data: body.data ?? body,
                message: body.message,
                passed: body.passed,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to approve evaluation');
        }
    }

    /**
     * Remediate: retake surveys or full training restart
     * @param {string} id
     * @param {{ mode: 'survey_retake'|'full_restart', notes?: string }} payload
     */
    async remediateEvaluation(id, payload) {
        try {
            const response = await this.api.client.post(
                `${this.api.basePath}/${id}/evaluation-remediate`,
                payload,
            );
            const body = response.data || {};
            return {
                success: true,
                data: body.data ?? body,
                message: body.message,
                mode: body.mode,
                assignResult: body.assignResult,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to remediate evaluation');
        }
    }

    /**
     * Bulk update results
     */
    async bulkUpdateResults(results) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/bulk-update-results`, { results });
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update results');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
